import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import Tooltip from "@mui/material/Tooltip";
import { formatLastCapture, formatTimestamp } from "./format";

const cellSx = { fontSize: { xs: "0.6rem", lg: "0.875rem" } };

// exactly two 24px icons plus the tightened padding; MUI's default 16px sides would
// leave 32px and wrap the second icon onto its own line
const STATUS_CELL_SX = { width: "3.5rem", px: "0.25rem" };

// the header defines each column's minimum width, so it is the one row that must
// never reflow: without this a column losing a few pixels breaks its label across
// four lines and the whole header block changes height
const headerSx = { whiteSpace: "nowrap", px: "0.5rem" };

// MUI's default 16px sides cost 32px per column, 224px across the seven text
// columns - a quarter of what the table needs, spent on whitespace. Halving it is
// the only way to buy width back without truncating anything.
const bodyCellSx = { px: "0.5rem" };

const COLUMNS = [
  "",
  "UUID",
  "Org",
  "State machine",
  "Firmware",
  "Last capture",
  "HW_ID",
  "Last alert",
];

const SORTABLE_COLUMNS = { "Last alert": "updated", Firmware: "firmware" };

// healthWarnings is every lifecycle finding that is true for the device right now, so
// the triangle follows the device's state rather than whether anyone has read an alert
// about it. More than one can hold at once.
const healthWarningText = (warnings) =>
  warnings
    .map((warning) => warning?.message)
    .filter(Boolean)
    .join(" \u00b7 ") || "Lifecycle warning";

const FleetTable = ({
  rows = [],
  sort = null,
  loading = false,
  error = null,
  onSortChange,
  onSelectDevice,
}) => {
  // Three states - asc, desc, then back to the flagged-first default - so that
  // default stays reachable. A two-state toggle stranded the user in an explicit
  // sort for the rest of the session, and flagged-first is the order this tab
  // exists to show. null sends no sort param, which the fleet endpoint reads as
  // its "default" mode.
  const handleSortClick = (field) => {
    if (sort === `${field}_asc`) {
      onSortChange?.(`${field}_desc`);
      return;
    }
    if (sort === `${field}_desc`) {
      onSortChange?.(null);
      return;
    }
    onSortChange?.(`${field}_asc`);
  };

  return (
    <TableContainer component={Paper} style={{ height: "65vh", width: "100%" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {COLUMNS.map((label) => {
              const field = SORTABLE_COLUMNS[label];
              return (
                <TableCell
                  key={label || "flag"}
                  align={label === "" ? "center" : "left"}
                  sx={label === "" ? { ...STATUS_CELL_SX, ...headerSx } : headerSx}
                >
                  {field ? (
                    <TableSortLabel
                      active={
                        sort === `${field}_asc` || sort === `${field}_desc`
                      }
                      direction={sort === `${field}_desc` ? "desc" : "asc"}
                      onClick={() => handleSortClick(field)}
                    >
                      {label}
                    </TableSortLabel>
                  ) : (
                    label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} align="center">
                {loading ? (
                  <CircularProgress size="1.5rem" sx={{ my: "1rem" }} />
                ) : (
                  <Typography sx={cellSx}>
                    {error
                      ? "Could not load devices. Retry in a moment."
                      : "No devices match this filter"}
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                hover
                className={"cursor-pointer"}
                onClick={() => onSelectDevice(row)}
                key={row.deviceId}
                sx={
                  row.flagged
                    ? { backgroundColor: "rgba(225, 0, 80, 0.08)" }
                    : {}
                }
              >
                <TableCell align="center" sx={STATUS_CELL_SX}>
                  {/* both can show at once - unread maintenance and an unread
                      lifecycle warning are different things. nowrap, or the two
                      icons stack and the row grows taller than every other one */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {row.flagged ? (
                      <PriorityHighIcon sx={{ color: "status.danger" }} />
                    ) : null}
                    {row.healthWarnings?.length ? (
                      <Tooltip title={healthWarningText(row.healthWarnings)}>
                        <WarningAmberIcon sx={{ color: "status.warning" }} />
                      </Tooltip>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.25rem",
                    }}
                  >
                    <Typography noWrap sx={cellSx}>
                      {row.deviceId}
                    </Typography>
                    {row.alertsMuted === true ? (
                      <Tooltip title="Chat and email alerts muted">
                        <NotificationsOffIcon
                          fontSize="small"
                          aria-label="Alerts muted"
                          sx={{ color: "text.secondary" }}
                        />
                      </Tooltip>
                    ) : null}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography noWrap sx={cellSx}>
                    {row.organizationId === "__unassigned__"
                      ? "No organization"
                      : row.organizationName || row.organizationId || "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography noWrap sx={cellSx}>
                    {row.desired?.state_machine ??
                      row.reported?.state_machine ??
                      "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography noWrap sx={cellSx}>
                    {row.desired?.firmware_version ??
                      row.reported?.firmware_version ??
                      "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography noWrap sx={cellSx}>
                    {formatLastCapture(row.lastCaptureAt)}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography noWrap sx={cellSx}>
                    {row.hardwareId || "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography noWrap sx={cellSx}>
                    {formatTimestamp(row.last_alert_timestamp)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FleetTable;
